import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'

// The advice StepScroll applies this horizontal gutter to every step's content.
// The tab strip breaks out of it (see Scroller) so the pills scroll to the
// screen edges rather than clipping at the content gutter.
const STEP_GUTTER = 20

// A single row of pill tabs that scrolls horizontally when the account tabs
// overflow the viewport. `-webkit-overflow-scrolling: touch` gives iOS momentum
// scrolling; the scrollbar is hidden so the row reads as a clean strip. The 2px
// vertical padding keeps the pill borders from being clipped at the edges.
//
// Full-bleed: negative horizontal margins cancel the StepScroll gutter so the
// scroll viewport spans the full screen width, while the matching inner padding
// keeps the first pill aligned with the page content and leaves a trailing gap
// after the last one. This makes the row read as scrolling off the screen edges
// instead of stopping at the content gutter.
const Scroller = styled.div`
  display: flex;
  align-items: center;
  /* Grow past the parent by both gutters (border-box, so the width includes the
     padding below) so the scroll viewport spans the full screen once the
     negative margins pull it out. */
  box-sizing: border-box;
  width: calc(100% + ${STEP_GUTTER * 2}px);
  column-gap: 8px;
  padding: 2px ${STEP_GUTTER}px;
  margin: 0 -${STEP_GUTTER}px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const TabButton = styled.button`
  flex: 0 0 auto;
  max-width: 220px;
  padding: 7px 30px;
  border-radius: 407px;
  background: none;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  ${p =>
    p.active
      ? css`
          color: ${p.theme.primaryColor};
          border: solid 2px ${p.theme.primaryLighter};
        `
      : css`
          color: ${p.theme.mediumGray};
          border: solid 2px ${p.theme.homeActionButtonBgColor};
        `};
`

const MobileTabs = ({ accounts, activeId, onChange, className }) => {
  const theme = useTheme()

  return (
    <Scroller className={className} role='tablist'>
      {accounts.map(account => (
        <TabButton
          key={account.id}
          type='button'
          role='tab'
          active={account.id === activeId}
          aria-selected={account.id === activeId}
          theme={theme}
          title={account.name}
          onClick={() => account.id !== activeId && onChange(account)}>
          {account.name}
        </TabButton>
      ))}
    </Scroller>
  )
}

MobileTabs.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string.isRequired })
  ).isRequired,
  activeId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
}

export default MobileTabs
